/**
 * Theme utility functions for component styling
 * 
 * SCALABLE VERSION: Uses CSS variables instead of switch statements.
 * These utilities now work with ANY number of themes automatically.
 * 
 * Components can use either:
 * 1. CSS utility classes (recommended): theme-bg, theme-text, etc.
 * 2. These JavaScript utilities (for backward compatibility)
 * 
 * NOTE: The theme parameter is kept for backward compatibility but is no longer
 * used in the implementation - all functions now return CSS classes that work
 * with any theme via CSS variables.
 */

import type { ThemeName } from './theme-config';

/**
 * Get theme-aware background color classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getBackgroundClasses(theme: ThemeName, variant: 'default' | 'card' | 'popover' | 'gradient' | 'page' = 'default'): string {
  // Use CSS utility classes that reference CSS variables
  // This works with ALL themes - no switch statement needed!
  switch (variant) {
    case 'card':
      return 'theme-bg-card';
    case 'popover':
      return 'theme-bg-popover';
    case 'gradient':
      return 'theme-bg-gradient';
    case 'page':
      return 'theme-bg-page';
    default:
      return 'theme-bg';
  }
}

/**
 * Get theme-aware text color classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getTextClasses(theme: ThemeName, variant: 'default' | 'muted' | 'subtle' = 'default'): string {
  // Use CSS utility classes that reference CSS variables
  switch (variant) {
    case 'muted':
      return 'theme-text-muted';
    case 'subtle':
      return 'theme-text-subtle';
    default:
      return 'theme-text';
  }
}

/**
 * Get theme-aware border color classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getBorderClasses(theme: ThemeName, variant: 'default' | 'subtle' | 'strong' = 'default'): string {
  // Use CSS utility classes that reference CSS variables
  switch (variant) {
    case 'subtle':
      return 'theme-border-subtle';
    case 'strong':
      return 'theme-border-strong';
    default:
      return 'theme-border';
  }
}

/**
 * Get theme-aware input classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getInputClasses(theme: ThemeName): string {
  // Use CSS utility class that references CSS variables
  return 'theme-input';
}

/**
 * Get theme-aware accent/button classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getAccentClasses(theme: ThemeName, variant: 'primary' | 'secondary' | 'muted' = 'primary'): string {
  // Use CSS utility classes that reference CSS variables
  switch (variant) {
    case 'primary':
      return 'theme-accent';
    case 'secondary':
      return 'theme-accent-muted';
    case 'muted':
      return 'theme-accent-muted';
    default:
      return 'theme-accent';
  }
}

/**
 * MDR-aligned secondary toolbar Button class names (Refresh, Export, etc.).
 * Use with `<Button variant="outline" className={getToolbarSecondaryButtonClasses(theme)}>`.
 * Sets card surface, theme text, and h-9 to match [components/managed-detection-response.tsx].
 */
export function getToolbarSecondaryButtonClasses(theme: ThemeName): string {
  return `${getBackgroundClasses(theme, 'card')} ${getTextClasses(theme)} h-9`;
}

/**
 * Get theme-aware selected/highlight classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getSelectedClasses(theme: ThemeName): string {
  // Use CSS utility class that references CSS variables
  return 'theme-selected';
}

/**
 * Get theme-aware hover classes
 * 
 * NOW USES CSS VARIABLES - works with all themes automatically!
 */
export function getHoverClasses(theme: ThemeName): string {
  // Use CSS utility class that references CSS variables
  return 'theme-hover';
}

/**
 * Combined utility: Get all common classes for a component
 */
export function getThemeClasses(theme: ThemeName, options: {
  background?: 'default' | 'card' | 'popover' | 'gradient';
  text?: 'default' | 'muted' | 'subtle';
  border?: 'default' | 'subtle' | 'strong';
  input?: boolean;
  accent?: 'primary' | 'secondary' | 'muted';
}): string {
  const classes: string[] = [];
  
  if (options.background) {
    classes.push(getBackgroundClasses(theme, options.background));
  }
  
  if (options.text) {
    classes.push(getTextClasses(theme, options.text));
  }
  
  if (options.border) {
    classes.push(getBorderClasses(theme, options.border));
  }
  
  if (options.input) {
    classes.push(getInputClasses(theme));
  }
  
  if (options.accent) {
    classes.push(getAccentClasses(theme, options.accent));
  }
  
  return classes.join(' ');
}
